/**
 * Screenshot Canvas Component
 *
 * Full-screen canvas for screenshot annotation using fabric.js.
 * Supports rectangle, arrow, text, and free drawing annotations.
 *
 * @module ScreenshotCanvas
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Canvas, Image, Rect, Line, IText, Polygon, Group, PencilBrush, type FabricObject } from 'fabric';
import { compressToWebP, autoResize } from './utils/screenshot';

/**
 * Annotation tool types
 */
export type AnnotationTool = 'select' | 'rectangle' | 'arrow' | 'text' | 'draw' | 'crop';

/**
 * Props for ScreenshotCanvas component
 */
export interface ScreenshotCanvasProps {
  /** Pre-captured screenshot image (base64) - captured before canvas opens */
  initialImage: string;

  /** Callback when user saves annotated screenshot (receives base64 WebP) */
  onSave: (annotatedScreenshot: string) => void;

  /** Callback when user cancels annotation */
  onCancel: () => void;
}

/**
 * History state for undo/redo (stored as ref to avoid re-renders)
 */
interface HistoryState {
  items: string[];
  step: number;
}

/**
 * Screenshot annotation canvas component
 *
 * Features:
 * - Captures viewport automatically on mount
 * - Annotation tools: rectangle, arrow, text, free drawing
 * - Color picker for annotations
 * - Undo/Redo functionality
 * - Clear all annotations
 * - Exports as WebP with compression
 * - Keyboard shortcuts (Esc, Ctrl+Z, Ctrl+Y)
 * - Mobile-optimized controls
 *
 * @example
 * ```tsx
 * <ScreenshotCanvas
 *   onSave={(base64) => {
 *     console.log('Screenshot saved:', base64);
 *     setScreenshot(base64);
 *   }}
 *   onCancel={() => {
 *     console.log('Annotation cancelled');
 *   }}
 * />
 * ```
 */
export const ScreenshotCanvas: React.FC<ScreenshotCanvasProps> = ({ initialImage, onSave, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Use ref for fabric canvas to avoid React state re-render issues with fabric.js
  const fabricCanvasRef = useRef<Canvas | null>(null);
  const [activeTool, setActiveTool] = useState<AnnotationTool>('rectangle');
  const [color, setColor] = useState<string>('#FF0000'); // Default: red
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Use ref for history to avoid dependency issues in useCallback/useEffect
  const historyRef = useRef<HistoryState>({ items: [], step: -1 });
  // State for UI updates only (undo/redo button states)
  const [historyStep, setHistoryStep] = useState<number>(-1);
  const [historyLength, setHistoryLength] = useState<number>(0);
  // Track if canvas is ready for interaction
  const [canvasReady, setCanvasReady] = useState(false);
  // Store background image reference for undo/redo (loadFromJSON doesn't preserve it)
  const backgroundImageRef = useRef<Image | null>(null);
  // Draggable toolbar state
  const [toolbarPosition, setToolbarPosition] = useState<{ x: number; y: number } | null>(null);
  const isDraggingToolbar = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Crop selection state - allows cancel before applying
  const [cropSelection, setCropSelection] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const cropRectRef = useRef<Rect | null>(null);

  /**
   * Save canvas state to history (using ref to avoid re-render issues)
   * Note: We exclude backgroundImage from serialization since we restore it manually
   */
  const saveState = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Get only the objects, not the background (we restore it separately)
    const objects = canvas.getObjects();
    const json = JSON.stringify({
      version: '6.0.0',
      objects: objects.map(obj => obj.toObject()),
    });
    const history = historyRef.current;
    const newItems = history.items.slice(0, history.step + 1);
    newItems.push(json);

    historyRef.current = { items: newItems, step: newItems.length - 1 };
    // Update UI state for button enables
    setHistoryStep(newItems.length - 1);
    setHistoryLength(newItems.length);
  }, []);

  /**
   * Initialize canvas with pre-captured screenshot on mount
   */
  useEffect(() => {
    const initCanvas = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setCanvasReady(false);

        // Validate initialImage
        if (!initialImage || !initialImage.startsWith('data:image/')) {
          throw new Error('Invalid screenshot image');
        }

        // Initialize fabric.js canvas
        if (!canvasRef.current) {
          throw new Error('Canvas element not found');
        }

        const canvas = new Canvas(canvasRef.current, {
          isDrawingMode: false,
          selection: true,
          backgroundColor: '#000000',
          // Fix for fabric.js v6 pointer-events issue
          enablePointerEvents: true,
        });

        // Store in ref immediately
        fabricCanvasRef.current = canvas;

        // Debug: extensive logging
        console.log('[ScreenshotCanvas] Fabric canvas created');
        console.log('[ScreenshotCanvas] Canvas wrapper and elements initialized', {
          hasWrapper: !!canvas.wrapperEl,
          hasUpperCanvas: !!canvas.upperCanvasEl,
          hasLowerCanvas: !!canvas.lowerCanvasEl,
        });

        // Fix fabric.js v6 pointer-events issue
        // Ensure upperCanvasEl has proper pointer-events CSS
        if (canvas.upperCanvasEl) {
          console.log('[ScreenshotCanvas] Fixing upper canvas pointer-events');
          // Force pointer-events to be auto (not none)
          canvas.upperCanvasEl.style.pointerEvents = 'auto';
          // Default: prevent browser gestures (will be changed dynamically for pan mode)
          canvas.upperCanvasEl.style.touchAction = 'none';
          // Use z-index lower than toolbar (100) to not block toolbar clicks
          canvas.upperCanvasEl.style.zIndex = '10';

          // Debug: Add native event listeners (don't log event objects as they have circular refs)
          canvas.upperCanvasEl.addEventListener('pointerdown', (e) => {
            console.log('[ScreenshotCanvas] NATIVE pointerdown on upper canvas!', {
              type: e.type,
              x: e.clientX,
              y: e.clientY,
              pointerId: (e as PointerEvent).pointerId,
            });
          });
          canvas.upperCanvasEl.addEventListener('mousedown', (e) => {
            console.log('[ScreenshotCanvas] NATIVE mousedown on upper canvas!', {
              type: e.type,
              x: e.clientX,
              y: e.clientY,
            });
          });

          // Log computed styles
          const styles = getComputedStyle(canvas.upperCanvasEl);
          console.log('[ScreenshotCanvas] Upper canvas computed styles:', {
            pointerEvents: styles.pointerEvents,
            position: styles.position,
            zIndex: styles.zIndex,
            display: styles.display,
            visibility: styles.visibility,
            left: styles.left,
            top: styles.top,
            width: styles.width,
            height: styles.height,
          });
        } else {
          console.error('[ScreenshotCanvas] ERROR: upperCanvasEl is null!');
        }

        // Check wrapper element and ensure proper z-index
        if (canvas.wrapperEl) {
          // Use z-index lower than toolbar to not block toolbar clicks
          canvas.wrapperEl.style.zIndex = '1';
          canvas.wrapperEl.style.position = 'relative';

          const wrapperStyles = getComputedStyle(canvas.wrapperEl);
          console.log('[ScreenshotCanvas] Wrapper computed styles:', {
            position: wrapperStyles.position,
            width: wrapperStyles.width,
            height: wrapperStyles.height,
            display: wrapperStyles.display,
            visibility: wrapperStyles.visibility,
            zIndex: wrapperStyles.zIndex,
          });
        }

        // Set canvas dimensions to viewport size
        const width = window.innerWidth;
        const height = window.innerHeight;
        canvas.setWidth(width);
        canvas.setHeight(height);

        // CRITICAL: Recalculate canvas offset for correct pointer coordinates
        // This must be called after setWidth/setHeight to update internal offset cache
        canvas.calcOffset();

        // Load pre-captured screenshot as background
        Image.fromURL(initialImage).then((img) => {
          if (!img) {
            setError('Failed to load screenshot');
            return;
          }

          // Scale image to fit canvas
          const scaleX = width / (img.width || 1);
          const scaleY = height / (img.height || 1);
          const scale = Math.min(scaleX, scaleY);

          img.set({
            scaleX: scale,
            scaleY: scale,
            selectable: false,
            evented: false,
          });

          canvas.backgroundImage = img;
          // Store reference for undo/redo restoration
          backgroundImageRef.current = img;
          canvas.renderAll();
          setIsLoading(false);
          setCanvasReady(true);

          // Debug: Log canvas ready state
          console.log('[ScreenshotCanvas] Image loaded, canvas ready');
          console.log('[ScreenshotCanvas] Canvas dimensions:', canvas.getWidth(), 'x', canvas.getHeight());

          // CRITICAL: Recalculate offset after image is loaded and visibility changes
          // This ensures getPointer() uses correct coordinates
          requestAnimationFrame(() => {
            canvas.calcOffset();
            console.log('[ScreenshotCanvas] calcOffset called after image load');
          });

          // Re-check upper canvas styles after visibility change
          if (canvas.upperCanvasEl) {
            const styles = getComputedStyle(canvas.upperCanvasEl);
            console.log('[ScreenshotCanvas] Upper canvas styles AFTER ready:', {
              pointerEvents: styles.pointerEvents,
              position: styles.position,
              visibility: styles.visibility,
              width: styles.width,
              height: styles.height,
            });
          }

          // Save initial state
          saveState();
        }).catch((err) => {
          console.error('[ScreenshotCanvas] Failed to load screenshot:', err);
          setError('Failed to load screenshot');
          setIsLoading(false);
        });
      } catch (err) {
        console.error('[ScreenshotCanvas] Initialization failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize canvas');
        setIsLoading(false);
      }
    };

    initCanvas();

    // Cleanup
    return () => {
      const canvas = fabricCanvasRef.current;
      if (canvas) {
        canvas.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, [initialImage, saveState]);

  /**
   * Create arrowhead polygon points
   * Returns points for a triangle arrowhead at the end of a line
   */
  const createArrowhead = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    headLength: number = 15
  ): { x: number; y: number }[] => {
    const angle = Math.atan2(y2 - y1, x2 - x1);

    // Calculate arrowhead points
    const x3 = x2 - headLength * Math.cos(angle - Math.PI / 6);
    const y3 = y2 - headLength * Math.sin(angle - Math.PI / 6);
    const x4 = x2 - headLength * Math.cos(angle + Math.PI / 6);
    const y4 = y2 - headLength * Math.sin(angle + Math.PI / 6);

    return [
      { x: x2, y: y2 },           // tip
      { x: x3, y: y3 },           // left wing
      { x: x4, y: y4 },           // right wing
    ];
  };

  /**
   * Handle tool change - uses ref to avoid re-render issues
   */
  useEffect(() => {
    const fabricCanvas = fabricCanvasRef.current;
    console.log('[ScreenshotCanvas] Tool effect running', { fabricCanvas: !!fabricCanvas, canvasReady, activeTool });
    if (!fabricCanvas || !canvasReady) return;

    // Disable browser gestures for annotation tools
    if (fabricCanvas.upperCanvasEl) {
      fabricCanvas.upperCanvasEl.style.touchAction = 'none';
      fabricCanvas.upperCanvasEl.style.pointerEvents = 'auto';
    }

    // Configure selection mode
    fabricCanvas.selection = activeTool === 'select';
    fabricCanvas.defaultCursor = 'default';
    fabricCanvas.hoverCursor = 'move';

    fabricCanvas.forEachObject((obj) => {
      obj.selectable = activeTool === 'select';
      obj.evented = true;
      obj.hoverCursor = 'move';
    });

    // Reset drawing mode
    fabricCanvas.isDrawingMode = activeTool === 'draw';

    // Initialize PencilBrush for free drawing mode (fabric.js v6 requires explicit creation)
    if (activeTool === 'draw') {
      if (!fabricCanvas.freeDrawingBrush) {
        console.log('[ScreenshotCanvas] Creating new PencilBrush');
        fabricCanvas.freeDrawingBrush = new PencilBrush(fabricCanvas);
      }
      fabricCanvas.freeDrawingBrush.color = color;
      fabricCanvas.freeDrawingBrush.width = 3;
    }

    // Handle object creation for other tools
    let isDrawing = false;
    let startX = 0;
    let startY = 0;
    let activeObject: FabricObject | null = null;
    // For arrow: store line and arrowhead separately during drawing
    let arrowLine: Line | null = null;
    let arrowHead: Polygon | null = null;

    const handleMouseDown = (e: any) => {
      console.log('[ScreenshotCanvas] mouse:down fired', { activeTool });
      if (activeTool === 'select' || activeTool === 'draw') return;

      const pointer = fabricCanvas.getPointer(e.e);
      console.log('[ScreenshotCanvas] pointer:', { x: pointer.x, y: pointer.y });
      isDrawing = true;
      startX = pointer.x;
      startY = pointer.y;

      // Create initial object
      switch (activeTool) {
        case 'rectangle':
          activeObject = new Rect({
            left: startX,
            top: startY,
            width: 0,
            height: 0,
            fill: 'transparent',
            stroke: color,
            strokeWidth: 3,
          });
          fabricCanvas.add(activeObject);
          break;

        case 'crop':
          activeObject = new Rect({
            left: startX,
            top: startY,
            width: 0,
            height: 0,
            fill: 'rgba(255, 255, 255, 0.1)',
            stroke: '#FFD700',
            strokeWidth: 2,
            strokeDashArray: [5, 5],
          });
          fabricCanvas.add(activeObject);
          break;

        case 'arrow':
          // Create line for arrow shaft
          arrowLine = new Line([startX, startY, startX, startY], {
            stroke: color,
            strokeWidth: 3,
            selectable: false,
            evented: false,
          });
          // Create arrowhead polygon
          const initialPoints = createArrowhead(startX, startY, startX, startY);
          arrowHead = new Polygon(initialPoints, {
            fill: color,
            stroke: color,
            strokeWidth: 1,
            selectable: false,
            evented: false,
          });
          fabricCanvas.add(arrowLine);
          fabricCanvas.add(arrowHead);
          break;

        case 'text':
          const text = new IText('Text', {
            left: startX,
            top: startY,
            fill: color,
            fontSize: 20,
            fontFamily: 'Arial',
          });
          fabricCanvas.add(text);
          fabricCanvas.setActiveObject(text);
          text.enterEditing();
          saveState();
          break;
      }

      fabricCanvas.renderAll();
    };

    const handleMouseMove = (e: any) => {
      if (!isDrawing || activeTool === 'text') return;

      const pointer = fabricCanvas.getPointer(e.e);

      switch (activeTool) {
        case 'rectangle':
        case 'crop':
          if (!activeObject) return;
          const rect = activeObject as Rect;
          rect.set({
            width: Math.abs(pointer.x - startX),
            height: Math.abs(pointer.y - startY),
            left: Math.min(pointer.x, startX),
            top: Math.min(pointer.y, startY),
          });
          break;

        case 'arrow':
          if (!arrowLine || !arrowHead) return;
          // Update line end position
          arrowLine.set({ x2: pointer.x, y2: pointer.y });
          // Update arrowhead position and rotation
          const newPoints = createArrowhead(startX, startY, pointer.x, pointer.y);
          arrowHead.set({ points: newPoints });
          // Force arrowhead to recalculate its dimensions
          arrowHead.setCoords();
          break;
      }

      fabricCanvas.renderAll();
    };

    const handleMouseUp = () => {
      if (isDrawing) {
        if (activeTool === 'rectangle' && activeObject) {
          saveState();
        } else if (activeTool === 'arrow' && arrowLine && arrowHead) {
          // Group line and arrowhead together for easier selection/manipulation
          const group = new Group([arrowLine, arrowHead], {
            selectable: true,
          });
          // Remove individual objects
          fabricCanvas.remove(arrowLine);
          fabricCanvas.remove(arrowHead);
          // Add grouped arrow
          fabricCanvas.add(group);
          saveState();
        } else if (activeTool === 'crop' && activeObject) {
          // Store crop selection - don't apply yet, wait for confirmation
          const rect = activeObject as Rect;
          const cropRect = {
            left: rect.left || 0,
            top: rect.top || 0,
            width: rect.width || 1,
            height: rect.height || 1,
          };
          // Only store if selection is meaningful (at least 10x10 pixels)
          if (cropRect.width >= 10 && cropRect.height >= 10) {
            setCropSelection(cropRect);
            cropRectRef.current = rect;
            // Keep the rectangle visible for user to see selection
          } else {
            // Too small, remove it
            fabricCanvas.remove(rect);
            fabricCanvas.renderAll();
          }
        }
      }
      isDrawing = false;
      activeObject = null;
      arrowLine = null;
      arrowHead = null;
    };

    console.log('[ScreenshotCanvas] Attaching mouse event handlers');
    fabricCanvas.on('mouse:down', handleMouseDown);
    fabricCanvas.on('mouse:move', handleMouseMove);
    fabricCanvas.on('mouse:up', handleMouseUp);

    return () => {
      console.log('[ScreenshotCanvas] Detaching mouse event handlers');
      fabricCanvas.off('mouse:down', handleMouseDown);
      fabricCanvas.off('mouse:move', handleMouseMove);
      fabricCanvas.off('mouse:up', handleMouseUp);
    };
  }, [canvasReady, activeTool, color, saveState]);

  /**
   * Handle multi-touch gestures (mobile zoom/pan)
   * Allow 2-finger gestures to bypass fabric.js for native zoom/pan
   */
  useEffect(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas || !canvasReady) return;

    const canvasEl = fabricCanvas.upperCanvasEl;
    if (!canvasEl) return;

    let isTwoFingerGesture = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        // Two fingers - enable native zoom/pan
        isTwoFingerGesture = true;
        canvasEl.style.pointerEvents = 'none'; // Disable fabric.js
        canvasEl.style.touchAction = 'pan-x pan-y pinch-zoom'; // Enable browser gestures
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2 && isTwoFingerGesture) {
        // Back to single touch - re-enable fabric.js
        isTwoFingerGesture = false;
        canvasEl.style.pointerEvents = 'auto'; // Re-enable fabric.js
        canvasEl.style.touchAction = 'none'; // Disable browser gestures
      }
    };

    canvasEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvasEl.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      canvasEl.removeEventListener('touchstart', handleTouchStart);
      canvasEl.removeEventListener('touchend', handleTouchEnd);
    };
  }, [canvasReady]);

  /**
   * Handle path created (for free drawing)
   */
  useEffect(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas || !canvasReady) return;

    const handlePathCreated = () => {
      saveState();
    };

    fabricCanvas.on('path:created', handlePathCreated);

    return () => {
      fabricCanvas.off('path:created', handlePathCreated);
    };
  }, [canvasReady, saveState]);

  /**
   * Handle undo
   */
  const handleUndo = useCallback(async () => {
    const fabricCanvas = fabricCanvasRef.current;
    const history = historyRef.current;

    if (history.step > 0 && fabricCanvas) {
      const prevStep = history.step - 1;
      const prevState = history.items[prevStep];

      try {
        // fabric.js v6 uses Promise-based API
        await fabricCanvas.loadFromJSON(prevState);
        // Restore background image (loadFromJSON doesn't preserve it)
        if (backgroundImageRef.current) {
          fabricCanvas.backgroundImage = backgroundImageRef.current;
        }
        fabricCanvas.renderAll();
        historyRef.current.step = prevStep;
        setHistoryStep(prevStep);
      } catch (err) {
        console.error('[ScreenshotCanvas] Undo failed:', err);
      }
    }
  }, []);

  /**
   * Handle redo
   */
  const handleRedo = useCallback(async () => {
    const fabricCanvas = fabricCanvasRef.current;
    const history = historyRef.current;

    if (history.step < history.items.length - 1 && fabricCanvas) {
      const nextStep = history.step + 1;
      const nextState = history.items[nextStep];

      try {
        // fabric.js v6 uses Promise-based API
        await fabricCanvas.loadFromJSON(nextState);
        // Restore background image (loadFromJSON doesn't preserve it)
        if (backgroundImageRef.current) {
          fabricCanvas.backgroundImage = backgroundImageRef.current;
        }
        fabricCanvas.renderAll();
        historyRef.current.step = nextStep;
        setHistoryStep(nextStep);
      } catch (err) {
        console.error('[ScreenshotCanvas] Redo failed:', err);
      }
    }
  }, []);

  /**
   * Crop canvas to a specific rectangle
   * Adjusts background image, canvas size, and all annotations
   */
  const handleCrop = useCallback((cropRect: { left: number; top: number; width: number; height: number }) => {
    const fabricCanvas = fabricCanvasRef.current;
    const bgImage = backgroundImageRef.current;
    if (!fabricCanvas || !bgImage) return;

    try {
      // Validate crop dimensions
      if (cropRect.width <= 0 || cropRect.height <= 0) {
        console.warn('[ScreenshotCanvas] Invalid crop dimensions');
        return;
      }

      // Get the background image's actual pixel dimensions and position
      const bgImg = bgImage;
      const bgWidth = (bgImg.width || 1) * (bgImg.scaleX || 1);
      const bgHeight = (bgImg.height || 1) * (bgImg.scaleY || 1);
      const bgLeft = bgImg.left || 0;
      const bgTop = bgImg.top || 0;

      // Create a temporary canvas to extract the cropped portion
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = Math.round(cropRect.width);
      tempCanvas.height = Math.round(cropRect.height);
      const ctx = tempCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context for crop');
      }

      // Load the background image using HTML Image element
      const htmlImg = new window.Image();
      const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
        htmlImg.onload = () => resolve(htmlImg);
        htmlImg.onerror = () => reject(new Error('Failed to load background image for crop'));
        htmlImg.crossOrigin = 'anonymous';
        htmlImg.src = initialImage; // Use the original initialImage to get raw pixels
      });

      loadPromise.then((img) => {
        // Calculate the source rectangle in the original image pixels
        // Account for the scaling and positioning of the fabric image
        const scaleX = img.width / bgWidth;
        const scaleY = img.height / bgHeight;

        const srcX = Math.round((cropRect.left - bgLeft) * scaleX);
        const srcY = Math.round((cropRect.top - bgTop) * scaleY);
        const srcWidth = Math.round(cropRect.width * scaleX);
        const srcHeight = Math.round(cropRect.height * scaleY);

        // Draw the cropped portion onto the temporary canvas
        ctx.drawImage(img, srcX, srcY, srcWidth, srcHeight, 0, 0, tempCanvas.width, tempCanvas.height);

        // Convert canvas to base64
        const croppedBase64 = tempCanvas.toDataURL('image/png');

        // Create new fabric Image from cropped data
        Image.fromURL(croppedBase64).then((newImg) => {
          if (!newImg) {
            throw new Error('Failed to create image from cropped data');
          }

          // Update background image reference
          newImg.set({
            scaleX: 1,
            scaleY: 1,
            left: 0,
            top: 0,
            selectable: false,
            evented: false,
          });

          if (fabricCanvas) {
            fabricCanvas.backgroundImage = newImg;
            backgroundImageRef.current = newImg;

            // Resize canvas to crop dimensions
            fabricCanvas.setWidth(cropRect.width);
            fabricCanvas.setHeight(cropRect.height);

            // Adjust all existing annotations by the crop offset
            fabricCanvas.forEachObject((obj: FabricObject) => {
              const objLeft = obj.left || 0;
              const objTop = obj.top || 0;
              obj.set({
                left: objLeft - cropRect.left,
                top: objTop - cropRect.top,
              });
              obj.setCoords();
            });

            // Re-render and save state
            fabricCanvas.renderAll();
            saveState();

            console.log('[ScreenshotCanvas] Crop applied successfully', {
              cropRect,
              newDimensions: { width: cropRect.width, height: cropRect.height },
            });
          }
        }).catch((err) => {
          console.error('[ScreenshotCanvas] Crop failed:', err);
          setError('Failed to apply crop');
        });
      }).catch((err) => {
        console.error('[ScreenshotCanvas] Load image for crop failed:', err);
        setError('Failed to load image for cropping');
      });
    } catch (err) {
      console.error('[ScreenshotCanvas] Crop operation failed:', err);
      setError(err instanceof Error ? err.message : 'Crop operation failed');
    }
  }, [initialImage, saveState]);

  /**
   * Clear all annotations
   */
  const handleClear = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;

    fabricCanvas.getObjects().forEach((obj: FabricObject) => {
      fabricCanvas.remove(obj);
    });
    fabricCanvas.renderAll();
    saveState();
  }, [saveState]);

  /**
   * Apply pending crop selection
   */
  const applyCrop = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!cropSelection || !fabricCanvas) return;

    // Remove the crop rectangle from canvas
    if (cropRectRef.current) {
      fabricCanvas.remove(cropRectRef.current);
      cropRectRef.current = null;
    }
    fabricCanvas.renderAll();

    // Apply the crop
    handleCrop(cropSelection);
    setCropSelection(null);
  }, [cropSelection, handleCrop]);

  /**
   * Cancel pending crop selection
   */
  const cancelCrop = useCallback(() => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;

    // Remove the crop rectangle from canvas
    if (cropRectRef.current) {
      fabricCanvas.remove(cropRectRef.current);
      cropRectRef.current = null;
    }
    fabricCanvas.renderAll();
    setCropSelection(null);
  }, []);

  /**
   * Save annotated screenshot
   */
  const handleSave = useCallback(async () => {
    const fabricCanvas = fabricCanvasRef.current;
    if (!fabricCanvas) return;

    try {
      setIsLoading(true);

      // Export canvas as base64 PNG
      const base64Png = fabricCanvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 1,
      });

      // Resize to max 1920x1080
      const resized = await autoResize(base64Png, 1920, 1080);

      // Compress to WebP
      const webp = await compressToWebP(resized, 0.8);

      onSave(webp);
    } catch (err) {
      console.error('[ScreenshotCanvas] Save failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to save screenshot');
      setIsLoading(false);
    }
  }, [onSave]);

  /**
   * Keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape: Cancel crop selection first, then close canvas
      if (e.key === 'Escape') {
        e.preventDefault();
        if (cropSelection) {
          cancelCrop();
        } else {
          onCancel();
        }
      }

      // Enter: Apply crop selection
      if (e.key === 'Enter' && cropSelection) {
        e.preventDefault();
        applyCrop();
      }

      // Ctrl+Z: Undo
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }

      // Ctrl+Y: Redo
      if (e.ctrlKey && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, handleUndo, handleRedo, cropSelection, cancelCrop, applyCrop]);

  /**
   * Toolbar drag handlers for mobile
   */
  const handleToolbarDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    // Don't start dragging if clicking on a button or input
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.closest('button') || target.closest('input')) {
      return;
    }

    e.preventDefault();
    isDraggingToolbar.current = true;

    const rect = toolbar.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    dragOffset.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const handleToolbarDrag = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDraggingToolbar.current) return;

    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    // Keep toolbar within viewport bounds
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    const maxX = window.innerWidth - toolbar.offsetWidth;
    const maxY = window.innerHeight - toolbar.offsetHeight;

    const newX = Math.max(0, Math.min(maxX, clientX - dragOffset.current.x));
    const newY = Math.max(0, Math.min(maxY, clientY - dragOffset.current.y));

    setToolbarPosition({ x: newX, y: newY });
  }, []);

  const handleToolbarDragEnd = useCallback(() => {
    isDraggingToolbar.current = false;
  }, []);

  // Add global drag listeners
  useEffect(() => {
    window.addEventListener('mousemove', handleToolbarDrag);
    window.addEventListener('mouseup', handleToolbarDragEnd);
    window.addEventListener('touchmove', handleToolbarDrag, { passive: false });
    window.addEventListener('touchend', handleToolbarDragEnd);

    return () => {
      window.removeEventListener('mousemove', handleToolbarDrag);
      window.removeEventListener('mouseup', handleToolbarDragEnd);
      window.removeEventListener('touchmove', handleToolbarDrag);
      window.removeEventListener('touchend', handleToolbarDragEnd);
    };
  }, [handleToolbarDrag, handleToolbarDragEnd]);

  return (
    <div
      ref={containerRef}
      className="bug-reporter-screenshot-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Maximum z-index to ensure canvas is above ALL elements on host page
        // Portal places this outside Shadow DOM in document.body
        zIndex: 2147483647,
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Toolbar - draggable on mobile */}
      <div
        ref={toolbarRef}
        className="bug-reporter-screenshot-toolbar"
        onMouseDown={handleToolbarDragStart}
        onTouchStart={handleToolbarDragStart}
        style={{
          position: 'absolute',
          ...(toolbarPosition
            ? { top: toolbarPosition.y, left: toolbarPosition.x, transform: 'none' }
            : { top: 16, left: '50%', transform: 'translateX(-50%)' }),
          display: 'flex',
          gap: 8,
          padding: 12,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          // z-index higher than canvas wrapper (1) to be above it
          zIndex: 100,
          cursor: 'grab',
          touchAction: 'none',
        }}
      >
        {/* Tool buttons - Order: Select, Zoom, Crop, [Divider], Rectangle, Arrow, Text, Draw, Pan */}

        {/* 1. Select */}
        <button
          type="button"
          className={`bug-reporter-tool-btn ${activeTool === 'select' ? 'active' : ''}`}
          onClick={() => setActiveTool('select')}
          title="Select (V)"
          style={{
            padding: 8,
            border: activeTool === 'select' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: activeTool === 'select' ? '#eff6ff' : 'white',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" strokeWidth="2" />
          </svg>
        </button>

        {/* 2. Crop */}
        <button
          type="button"
          className={`bug-reporter-tool-btn ${activeTool === 'crop' ? 'active' : ''}`}
          onClick={() => { cancelCrop(); setActiveTool('crop'); }}
          title="Crop (C) - Select area, Enter to apply, Esc to cancel"
          style={{
            padding: 8,
            border: activeTool === 'crop' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: activeTool === 'crop' ? '#eff6ff' : 'white',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M6 9V3h3M18 9V3h-3M6 15v6h3M18 15v6h-3M3 6h3M3 18h3M21 6h-3M21 18h-3" strokeWidth="2" />
          </svg>
        </button>

        {/* Crop confirm/cancel buttons - shown when crop selection is active */}
        {cropSelection && (
          <>
            <button
              type="button"
              onClick={applyCrop}
              title="Apply Crop (Enter)"
              style={{
                padding: '4px 8px',
                border: 'none',
                borderRadius: 4,
                backgroundColor: '#10b981',
                color: 'white',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              ✓
            </button>
            <button
              type="button"
              onClick={cancelCrop}
              title="Cancel Crop (Esc)"
              style={{
                padding: '4px 8px',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                backgroundColor: 'white',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              ✕
            </button>
          </>
        )}

        {/* Divider after navigation tools */}
        <div style={{ width: 1, backgroundColor: '#e5e7eb', margin: '4px 0' }} />

        {/* 4. Rectangle */}
        <button
          type="button"
          className={`bug-reporter-tool-btn ${activeTool === 'rectangle' ? 'active' : ''}`}
          onClick={() => setActiveTool('rectangle')}
          title="Rectangle (R)"
          style={{
            padding: 8,
            border: activeTool === 'rectangle' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: activeTool === 'rectangle' ? '#eff6ff' : 'white',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" strokeWidth="2" />
          </svg>
        </button>

        {/* 5. Arrow */}
        <button
          type="button"
          className={`bug-reporter-tool-btn ${activeTool === 'arrow' ? 'active' : ''}`}
          onClick={() => setActiveTool('arrow')}
          title="Arrow (A)"
          style={{
            padding: 8,
            border: activeTool === 'arrow' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: activeTool === 'arrow' ? '#eff6ff' : 'white',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M5 12h14M12 5l7 7-7 7" strokeWidth="2" />
          </svg>
        </button>

        {/* 6. Text */}
        <button
          type="button"
          className={`bug-reporter-tool-btn ${activeTool === 'text' ? 'active' : ''}`}
          onClick={() => setActiveTool('text')}
          title="Text (T)"
          style={{
            padding: 8,
            border: activeTool === 'text' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: activeTool === 'text' ? '#eff6ff' : 'white',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M4 7V4h16v3M9 20h6M12 4v16" strokeWidth="2" />
          </svg>
        </button>

        {/* 6. Free Draw */}
        <button
          type="button"
          className={`bug-reporter-tool-btn ${activeTool === 'draw' ? 'active' : ''}`}
          onClick={() => setActiveTool('draw')}
          title="Free Draw (D)"
          style={{
            padding: 8,
            border: activeTool === 'draw' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: activeTool === 'draw' ? '#eff6ff' : 'white',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 19l7-7 3 3-7 7-3-3z" strokeWidth="2" />
            <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" strokeWidth="2" />
          </svg>
        </button>

        {/* Color picker */}
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          title="Color"
          style={{
            width: 40,
            height: 36,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        />

        {/* Undo/Redo */}
        <button
          type="button"
          onClick={handleUndo}
          disabled={historyStep <= 0}
          title="Undo (Ctrl+Z)"
          style={{
            padding: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: 'white',
            cursor: historyStep <= 0 ? 'not-allowed' : 'pointer',
            opacity: historyStep <= 0 ? 0.5 : 1,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 7v6h6M21 17a9 9 0 00-9-9 9 9 0 00-9 9" strokeWidth="2" />
          </svg>
        </button>

        <button
          type="button"
          onClick={handleRedo}
          disabled={historyStep >= historyLength - 1}
          title="Redo (Ctrl+Y)"
          style={{
            padding: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: 'white',
            cursor: historyStep >= historyLength - 1 ? 'not-allowed' : 'pointer',
            opacity: historyStep >= historyLength - 1 ? 0.5 : 1,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 7v6h-6M3 17a9 9 0 019-9 9 9 0 019 9" strokeWidth="2" />
          </svg>
        </button>

        <button
          type="button"
          onClick={handleClear}
          title="Clear All"
          style={{
            padding: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            backgroundColor: 'white',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeWidth="2" />
          </svg>
        </button>

        {/* Divider */}
        <div style={{ width: 1, backgroundColor: '#e5e7eb', margin: '4px 0' }} />

        {/* Save - green checkmark */}
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          title="Save"
          style={{
            padding: 8,
            border: 'none',
            borderRadius: 4,
            backgroundColor: '#10b981',
            color: 'white',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </button>

        {/* Cancel - red X */}
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          title="Cancel"
          style={{
            padding: 8,
            border: 'none',
            borderRadius: 4,
            backgroundColor: '#ef4444',
            color: 'white',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Canvas wrapper - provides dimensions for fabric.js wrapper */}
      {/* NOTE: Use visibility instead of display:none to allow fabric.js to measure dimensions during init */}
      {/* CRITICAL: Do NOT set CSS width/height on canvas - fabric.js sets internal dimensions */}
      {/* CSS scaling causes getPointer() coordinate mismatch */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          visibility: isLoading || error ? 'hidden' : 'visible',
          overflow: 'hidden',
          backgroundColor: '#1a1a1a',
          zIndex: 1,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            /* DO NOT use width/height: 100% - causes fabric.js pointer coordinate mismatch */
            /* fabric.js sets canvas dimensions via setWidth/setHeight which sets both */
            /* the internal backstore AND the CSS dimensions correctly */
          }}
        />
      </div>

      {/* Loading overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            fontSize: 18,
          }}
        >
          Loading screenshot editor...
        </div>
      )}

      {/* Error message */}
      {error && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: 20,
          }}
        >
          <p style={{ fontSize: 18, marginBottom: 16 }}>Error: {error}</p>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 4,
              backgroundColor: '#ef4444',
              color: 'white',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};
